/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

import Title from "./ui/Title";
import Text from "./ui/Text";
import Flex from "./ui/Flex";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import Stack from "./ui/Stack";
import LoadingIndicator from "./LoadingIndicator";
import NavigationDropdown from "./NavigationDropdown";
import ClientProfilePopover from "./client/ClientProfilePopover";
import ClientLoginPopover from "./client/ClientLoginPopover";
import CartDrawer from "./cart/CartDrawer";

import { ROUTES } from "../constants/constants";
import { useAuth } from "../hooks/useAuth";
import { useBooking } from "../hooks/useBooking";
import { useStepManager } from "../hooks/useStepManager";
import { useNavigationItems } from "../hooks/useNavigationItems";
import { useCustomFeatures } from "../hooks/useCustomFeatures";
import usePluginUtils from "../hooks/usePluginUtils";
import { useCompany } from "../lib/react-query/useCompany";
import { useConfig } from "../lib/react-query/useConfig";
import { useLocations } from "../lib/react-query/useLocations";
import { useTranslate } from "../hooks/useTranslate";
import { useClickableA11y } from "../hooks/a11y/useClickableA11y";

const StyledHeader = styled(Flex)`
  margin-bottom: 1rem;
  min-height: 50px;
`;

const HeaderRight = styled(Flex)`
  margin-left: auto;
`;

const ClientLoginControl = styled.div`
  margin-left: auto;
`;

const LocationTitleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: ${(props) => (props.$isSelectable ? "pointer" : "default")};
`;

const LocationDescription = styled(Text)`
  margin-top: 4px;
`;

const LocationBlock = ({ selectable }) => {
  const { _t } = useTranslate();
  const { isPluginEnabled } = usePluginUtils();
  const { data: company } = useCompany();
  const { data: config } = useConfig();
  const { selectedLocation } = useBooking();
  const { navigateToStep } = useStepManager();
  const { data: locations } = useLocations({
    enabled: isPluginEnabled("location") && selectable,
  });

  const handleLocationClick = useCallback(() => {
    if (!selectable) return;
    if (!locations || locations.length <= 1) return;
    navigateToStep(ROUTES.LOCATIONS);
  }, [selectable, locations, navigateToStep]);

  const showChevron = Boolean(selectable && locations && locations.length > 1);
  const locationA11yProps = useClickableA11y({ onClick: showChevron ? handleLocationClick : undefined });

  if (isPluginEnabled("location") && selectedLocation) {
    return (
      <Stack $gap="0">
        <LocationTitleWrapper
          $isSelectable={showChevron}
          onClick={handleLocationClick}
          {...locationA11yProps}
          aria-label={showChevron ? _t("Change location") : undefined}
        >
          <Title size="h3">{selectedLocation.name}</Title>
          {showChevron && <Icon.ChevronDown />}
        </LocationTitleWrapper>
        {selectedLocation.full_address && (
          <LocationDescription size="small" lineClamp={1}>{selectedLocation.full_address}</LocationDescription>
        )}
      </Stack>
    );
  }

  if (!isPluginEnabled("location") && config.skip_address !== "1") {
    return (
      <Stack $gap="0">
        {company.city && <Title size="h3">{company.city}</Title>}
        {(company.address1 || company.address2) && (
          <LocationDescription size="small" lineClamp={1}>{company.address1 || company.address2}</LocationDescription>
        )}
      </Stack>
    );
  }

  return null;
};

const NavigationHeader = ({
  title,
  subtitle,
  location: showLocation = false,
  locationSelectable = false,
  back,
  fallbackRoute = ROUTES.HOME,
  navigation = false,
  cart = false,
  clientLogin = false,
  rightContent,
}) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { _t } = useTranslate();
  const { isPluginEnabled } = usePluginUtils();
  const { isAuthenticated } = useAuth();
  const { navigateToPreviousStep } = useStepManager();
  const customFeatures = useCustomFeatures();
  const { showCart, isLoading: isCustomFeaturesLoading } = customFeatures;
  const navigationItems = useNavigationItems(customFeatures);
  const { isLoading: isLocationsLoading } = useLocations({
    enabled: showLocation && locationSelectable && isPluginEnabled("location"),
  });

  const handleBack = useCallback(() => {
    if (back === "step") {
      navigateToPreviousStep();
      return;
    }

    if (back === -1) {
      if (routerLocation.key !== "default") {
        navigate(-1);
      } else {
        navigate(fallbackRoute);
      }
      return;
    }

    if (typeof back === "string") {
      navigate(back);
    }
  }, [back, navigate, navigateToPreviousStep, fallbackRoute, routerLocation.key]);

  const waitForLocations = showLocation && locationSelectable && isLocationsLoading;
  const waitForCustomFeatures = cart && isCustomFeaturesLoading;

  if (waitForLocations || waitForCustomFeatures) {
    return (
      <StyledHeader as="header" $justify="center" $align="center">
        <LoadingIndicator size="md" />
      </StyledHeader>
    );
  }

  const showBack = back !== undefined && back !== null && back !== false;
  const showNavDropdown = navigation && navigationItems.length > 1;
  const showCartSlot = cart && showCart;
  const showClientLogin = clientLogin && isPluginEnabled("client_login");

  return (
    <StyledHeader as="header" $justify="space-between" $align="center" $gap="1rem">
      <Flex $align="center" $gap="0.5rem">
        {showBack && (
          <Button
            variant="transparent"
            size="icon"
            icon={<Icon.NavigateLeft size={Icon.SIZE.SM} />}
            aria-label={_t("Back")}
            onClick={handleBack}
          />
        )}
        {showNavDropdown && <NavigationDropdown navigationItems={navigationItems} />}
        {title != null ? (
          <Stack $direction="column" $gap="0.25rem">
            <Title size="h3">{title}</Title>
            {subtitle && <Text size="small">{subtitle}</Text>}
          </Stack>
        ) : (
          showLocation && <LocationBlock selectable={locationSelectable} />
        )}
      </Flex>
      <HeaderRight $align="flex-start" $gap="0.5rem">
        {rightContent}
        {showCartSlot && <CartDrawer />}
        {showClientLogin && (
          <ClientLoginControl>
            {isAuthenticated ? <ClientProfilePopover /> : <ClientLoginPopover />}
          </ClientLoginControl>
        )}
      </HeaderRight>
    </StyledHeader>
  );
};

export default NavigationHeader;
